/**
 * exportUtils.ts — Client-side export helpers.
 *
 * - printDocument  : Opens a print-ready page in a new tab and triggers window.print().
 *                    User saves as PDF from the browser dialog. Zero dependencies.
 * - downloadCSV    : Builds a CSV string and triggers a browser download.
 * - downloadJSON   : JSON.stringify + browser download.
 */

// ─── Print / PDF ──────────────────────────────────────────────────────────────

const PRINT_STYLES = `
  *, *::before, *::after { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 13px;
    line-height: 1.6;
    color: #111;
    padding: 32px 40px;
    max-width: 800px;
    margin: 0 auto;
  }
  h1 { font-size: 20px; font-weight: 700; margin: 0 0 4px; }
  h2 { font-size: 15px; font-weight: 600; margin: 24px 0 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  h3 { font-size: 13px; font-weight: 600; margin: 16px 0 6px; }
  p  { margin: 0 0 8px; }
  ul { padding-left: 18px; margin: 0 0 8px; }
  li { margin-bottom: 4px; }
  .meta { font-size: 11px; color: #6b7280; margin-bottom: 20px; }
  .badge {
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 9999px;
    border: 1px solid;
    text-transform: capitalize;
  }
  .badge-high   { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
  .badge-medium { background: #fffbeb; color: #92400e; border-color: #fde68a; }
  .badge-low    { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
  .badge-active { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
  .badge-draft  { background: #f9fafb; color: #374151; border-color: #d1d5db; }
  .badge-completed { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
  .section { margin-bottom: 20px; }
  .card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 14px; margin-bottom: 8px; }
  .card-title { font-weight: 600; font-size: 13px; }
  .card-meta  { font-size: 11px; color: #6b7280; margin-top: 2px; }
  .level-bar { display: flex; gap: 3px; margin-top: 4px; }
  .level-cell { width: 16px; height: 8px; border-radius: 2px; }
  .level-filled   { background: #3b82f6; }
  .level-target   { background: #bfdbfe; }
  .level-empty    { background: #f3f4f6; }
  .objective { border-left: 3px solid #93c5fd; padding-left: 12px; margin-bottom: 12px; }
  .resources { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
  .resource-tag { font-size: 10px; background: #f3f4f6; color: #374151; padding: 1px 6px; border-radius: 4px; }
  .milestone { display: flex; align-items: flex-start; gap: 10px; padding: 8px 12px; border: 1px solid #e5e7eb; border-radius: 6px; margin-bottom: 6px; }
  .milestone-num { width: 20px; height: 20px; border-radius: 50%; background: #eff6ff; color: #1d4ed8; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
  .summary-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 16px; margin-bottom: 16px; }
  .rec-item { display: flex; gap: 8px; margin-bottom: 4px; }
  .rec-arrow { color: #9ca3af; flex-shrink: 0; }
  .confidence { font-size: 11px; color: #6b7280; }
  @media print {
    body { padding: 0; }
    @page { margin: 20mm; }
  }
`;

/**
 * Opens a print-ready HTML page in a new tab and auto-triggers the print dialog.
 * User saves as PDF from the browser.
 *
 * @param bodyHtml  Inner HTML to inject (should use the .card/.section/etc classes above)
 * @param title     Used as <title> and shown in the browser tab
 */
export function printDocument(bodyHtml: string, title: string): void {
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
${bodyHtml}
<script>
  window.onload = function() {
    setTimeout(function() { window.print(); }, 300);
  };
</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    // Popup blocked — fallback: download as HTML file
    downloadBlob(html, `${title}.html`, 'text/html');
    return;
  }
  win.document.write(html);
  win.document.close();
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

/**
 * Builds a RFC-4180 CSV string from headers + rows and triggers a browser download.
 */
export function downloadCSV(headers: string[], rows: string[][], filename: string): void {
  const escape = (v: string) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))];
  downloadBlob(lines.join('\r\n'), filename, 'text/csv;charset=utf-8;');
}

// ─── JSON ─────────────────────────────────────────────────────────────────────

/** Serialises data as pretty-printed JSON and triggers a browser download. */
export function downloadJSON(data: unknown, filename: string): void {
  downloadBlob(JSON.stringify(data, null, 2), filename, 'application/json');
}

// ─── Internal helpers ────────────────────────────────────────────────────────

function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ─── IDP print HTML builder ───────────────────────────────────────────────────

import type { IDPPlan } from './idp';

export function buildIDPPrintHTML(plan: IDPPlan, employeeName: string): string {
  const statusClass = `badge-${plan.status}`;
  const generatedAt = new Date(plan.generatedAt).toLocaleString();

  // Filter out any gaps where current >= target (same safety net as the live UI)
  const filteredGaps = plan.competencyGaps.filter((gap) => gap.currentLevel < gap.targetLevel);
  const gaps = filteredGaps.map((gap) => {
    const cells = [1, 2, 3, 4, 5].map((lvl) => {
      const cls = lvl <= gap.currentLevel ? 'level-filled' : lvl <= gap.targetLevel ? 'level-target' : 'level-empty';
      return `<div class="level-cell ${cls}"></div>`;
    }).join('');
    return `
      <div class="card">
        <div style="display:flex;align-items:center;justify-content:space-between">
          <span class="card-title">${escapeHtml(gap.name)} <span style="font-weight:400;color:#6b7280;font-size:11px">(${escapeHtml(gap.code)})</span></span>
          <span class="badge badge-${gap.severity}">${escapeHtml(gap.severity)}</span>
        </div>
        <div class="level-bar">${cells}</div>
        <div class="card-meta">Level ${gap.currentLevel} → Level ${gap.targetLevel}</div>
      </div>`;
  }).join('');

  const objectives = plan.objectives.map((obj) => `
    <div class="objective">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
        <span class="badge badge-active">${escapeHtml(obj.competency)}</span>
        <span style="font-size:11px;color:#6b7280">${escapeHtml(obj.timeline)}</span>
      </div>
      <p style="margin:0 0 6px;font-size:13px">${escapeHtml(obj.goal)}</p>
      ${obj.resources.length > 0 ? `<div class="resources">${obj.resources.map((r) => `<span class="resource-tag">${escapeHtml(r)}</span>`).join('')}</div>` : ''}
    </div>`).join('');

  const milestones = plan.milestones.map((m, i) => `
    <div class="milestone">
      <div class="milestone-num">${i + 1}</div>
      <div style="flex:1">
        <p style="margin:0;font-size:13px">${escapeHtml(m.label)}</p>
        ${m.dueDate ? `<div class="card-meta">Due: ${new Date(m.dueDate).toLocaleDateString()}</div>` : ''}
      </div>
      <span class="badge badge-${m.status === 'in-progress' ? 'medium' : m.status === 'completed' ? 'completed' : 'draft'}">${escapeHtml(m.status)}</span>
    </div>`).join('');

  const isAdvancementReady = plan.readyForAdvancement === true || (plan.competencyGaps.length > 0 && filteredGaps.length === 0);

  const gapSectionTitle = isAdvancementReady && filteredGaps.length > 0 ? 'Next Role Gap Analysis' : 'Competency Gaps';
  const advancementBanner = isAdvancementReady
    ? `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 16px;margin-bottom:12px;display:flex;gap:12px;align-items:flex-start">
        <span style="font-size:18px">&#x1F3C6;</span>
        <div>
          <p style="margin:0;font-weight:600;color:#166534;font-size:13px">Ready for Advancement</p>
          <p style="margin:4px 0 0;color:#15803d;font-size:11px">${filteredGaps.length > 0 ? 'Current role requirements fully met. Gaps below show what is needed for the next role.' : 'All competency requirements met or exceeded. Employee is a candidate for promotion.'}</p>
        </div>
      </div>`
    : '';

  return `
    <h1>${escapeHtml(employeeName)}'s Individual Development Plan</h1>
    <div class="meta">Generated ${escapeHtml(generatedAt)} &nbsp;·&nbsp; <span class="badge ${statusClass}">${escapeHtml(plan.status)}</span></div>

    <div class="section">
      <h2>${gapSectionTitle}</h2>
      ${advancementBanner}
      ${filteredGaps.length > 0 ? gaps : (isAdvancementReady ? '' : '<p style="color:#9ca3af">No competency gaps identified.</p>')}
    </div>

    <div class="section">
      <h2>${isAdvancementReady ? 'Stretch Goals for Next Role' : 'Development Objectives'}</h2>
      ${plan.objectives.length > 0 ? objectives : '<p style="color:#9ca3af">No objectives defined.</p>'}
    </div>

    <div class="section">
      <h2>${isAdvancementReady ? 'Advancement Milestones' : '30 / 60 / 90 Day Milestones'}</h2>
      ${plan.milestones.length > 0 ? milestones : '<p style="color:#9ca3af">No milestones defined.</p>'}
    </div>`;
}

// ─── AI Analysis print HTML builder ──────────────────────────────────────────

import type { AIAnalysisResult, AIReportResult } from './aiQueue';

export function buildAnalysisPrintHTML(result: AIAnalysisResult, employeeName?: string): string {
  const analyzedAt = new Date(result.analyzedAt).toLocaleString();
  const priorityColor = { high: '#dc2626', medium: '#d97706', low: '#16a34a' };
  const riskColor = { high: '#dc2626', medium: '#d97706', low: '#16a34a' };

  const insights = result.insights.map((i) => `
    <li style="margin-bottom:6px;display:flex;gap:8px">
      <span style="color:#9ca3af;flex-shrink:0">→</span>
      <span>${escapeHtml(i)}</span>
    </li>`).join('');

  const risks = result.riskFlags.map((r) => `
    <li style="margin-bottom:4px;display:flex;gap:8px">
      <span style="flex-shrink:0">⚠</span>
      <span>${escapeHtml(r)}</span>
    </li>`).join('');

  return `
    <h1>${employeeName ? escapeHtml(employeeName) + ' — ' : ''}Employee Analysis</h1>
    <div class="meta">Analyzed ${escapeHtml(analyzedAt)}</div>

    <div class="summary-box" style="display:flex;gap:24px;flex-wrap:wrap">
      <div>
        <span style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.05em">Development Priority</span>
        <p style="margin:4px 0 0;font-weight:600;color:${priorityColor[result.developmentPriority]}">${escapeHtml(result.developmentPriority)}</p>
      </div>
      <div>
        <span style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.05em">Retention Risk</span>
        <p style="margin:4px 0 0;font-weight:600;color:${riskColor[result.retentionRisk]}">${escapeHtml(result.retentionRisk)}</p>
      </div>
    </div>

    ${result.insights.length > 0 ? `
    <div class="section">
      <h2>Insights</h2>
      <ul style="list-style:none;padding:0;margin:0">${insights}</ul>
    </div>` : ''}

    ${result.riskFlags.length > 0 ? `
    <div class="section" style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:14px 16px">
      <h2 style="color:#92400e">Risk Flags</h2>
      <ul style="list-style:none;padding:0;margin:0;color:#b45309">${risks}</ul>
    </div>` : ''}`;
}

// ─── AI Report print HTML builder ────────────────────────────────────────────

export function buildReportPrintHTML(result: AIReportResult): string {
  const generatedAt = new Date(result.generatedAt).toLocaleString();
  const confidence = Math.round(result.confidenceScore * 100);

  const sections = result.sections.map((s) => `
    <div class="section" style="border-left:3px solid #e5e7eb;padding-left:14px;margin-bottom:16px">
      <h3>${escapeHtml(s.heading)}</h3>
      <p>${escapeHtml(s.content)}</p>
    </div>`).join('');

  const recs = result.recommendations.length > 0 ? `
    <div class="section">
      <h2>Recommendations</h2>
      ${result.recommendations.map((r) => `
        <div class="rec-item">
          <span class="rec-arrow">→</span>
          <span>${escapeHtml(r)}</span>
        </div>`).join('')}
    </div>` : '';

  return `
    <h1>${escapeHtml(result.title)}</h1>
    <div class="meta">Generated ${escapeHtml(generatedAt)} &nbsp;·&nbsp; <span class="confidence">Confidence: ${confidence}%</span></div>

    <div class="summary-box">
      <h3 style="margin:0 0 6px">Executive Summary</h3>
      <p style="margin:0">${escapeHtml(result.summary)}</p>
    </div>

    ${sections}
    ${recs}`;
}
